import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import chevronUrl from '@/assets/icons/chevron.svg?url';

const ctaGradient = keyframes`
  0% {
    background-position: 25% 50%;
  }
  100% {
    background-position: 75% 50%;
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const StyledButton = styled.button<{ $disabled?: boolean; $variant?: 'default' | 'green' }>`
  width: 100%;
  padding: 14px 8px;
  border-radius: 16px;
  background: ${({ $disabled, $variant }) => {
    if ($disabled) return '#94a3b8';
    if ($variant === 'green') return '#22c55e';
    return 'linear-gradient(90deg, #54c7fa, #29b9f8)';
  }};
  background-size: ${({ $variant }) => ($variant === 'green' ? '100% 100%' : '260% 100%')};
  animation: ${({ $disabled, $variant }) =>
    $disabled || $variant === 'green' ? 'none' : ctaGradient} 4.2s ease-in-out infinite alternate;
  will-change: background-position;
  transform: translateZ(0);
  color: white;
  font-size: 21px;
  line-height: 28px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-align: center;
  border: none;
  outline: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  -webkit-tap-highlight-color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 56px;
  transition: all 0.2s ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  filter: ${({ $disabled }) => ($disabled ? 'grayscale(20%)' : 'none')};

  &:focus,
  &:active {
    outline: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }
`;

const Arrow = styled.img`
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface CtaButtonProps {
  label?: string;
  showArrow?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children?: ReactNode;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'default' | 'green';
}

export function CtaButton({
  label = '',
  showArrow = false,
  loading = false,
  disabled = false,
  onClick,
  children,
  type = 'button',
  variant = 'default',
}: CtaButtonProps) {
  const handleClick = () => {
    if (loading || disabled) return;

    try {
      if ('vibrate' in navigator) {
        navigator.vibrate(15);
      }
    }
    catch {
      /* noop */
    }
    onClick?.();
  };

  return (
    <StyledButton onClick={handleClick} disabled={loading || disabled} $disabled={loading || disabled} $variant={variant} type={type}>
      {loading
        ? (
            <Spinner key="loading" />
          )
        : (
            <span key={label || 'text'}>{children || label}</span>
          )}
      {showArrow && <Arrow src={chevronUrl} width={22} height={22} alt="next" aria-hidden="true" />}
    </StyledButton>
  );
}
